import type { RefObject } from 'react'
import { useEffect, useRef } from 'react'

export type UseClickOutsideOptions = {
  additionalRoots?: ReadonlyArray<RefObject<HTMLElement | null>>
}

export default function useClickOutside<T extends HTMLElement>(
  isOpen: boolean,
  callback: () => void,
  options?: UseClickOutsideOptions
) {
  const ref = useRef<T>(null)
  const optionsRef = useRef(options)

  useEffect(() => {
    optionsRef.current = options
  }, [options])

  useEffect(() => {
    const documentListener = (event: MouseEvent) => {
      const target = event.target as Node
      if (!ref.current || ref.current.contains(target)) {
        return
      }
      for (const root of optionsRef.current?.additionalRoots ?? []) {
        if (root.current?.contains(target)) {
          return
        }
      }

      callback()
    }

    if (isOpen) {
      document.addEventListener('click', documentListener)
    }

    return () => {
      document.removeEventListener('click', documentListener)
    }
  }, [callback, isOpen])

  return ref
}
